import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider as TanStackRouterProvider, createBrowserHistory, createRouter } from '@tanstack/react-router';
import {
  WORKSPACE_PATH_PREFIX,
  resolveActiveWorkspace,
  useAccessContext,
  useClearWorkspaceCacheOnChange,
} from '@vizit/access';
import { APIError, APIErrorType, type AuthContextType, useAuth } from '@vizit/auth';
import * as React from 'react';
import { type AnalyticsContextType, useAnalytics } from '../../analytics';
import { routeTree } from '../../routeTree.gen';
import type { RouteAccessContext, RouteContext } from './types';

// Router Context
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        if (
          error instanceof APIError &&
          (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.UnprocessableContent)
        ) {
          Sentry.addBreadcrumb({
            message: 'Skipping query retry due to auth error',
            level: 'info',
            data: { error_type: error.type },
          });
          return false;
        }

        if (error instanceof APIError && error.status === 404) {
          Sentry.addBreadcrumb({
            message: 'Skipping query retry due to 404 error',
            level: 'info',
            data: { error_status: error.status },
          });
          return false;
        }

        return failureCount <= 3; // Retry up to 3 times
      },
      refetchOnWindowFocus: (error) => {
        if (
          error instanceof APIError &&
          error.status === 404 &&
          (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.UnprocessableContent)
        ) {
          return false;
        }

        return true;
      },
    },
  },
});

const context: RouteContext = { queryClient };

// Router
const history = createBrowserHistory();

const router = createRouter({
  context,
  defaultPreload: 'intent',
  history,
  notFoundMode: 'root',
  routeTree,
  scrollRestoration: true,
});

const WORKSPACE_PATHNAME_PATTERN = new RegExp(`^${WORKSPACE_PATH_PREFIX}/([^/]+)`);

function getWorkspaceSlugFromPathname(pathname: string): string | undefined {
  return pathname.match(WORKSPACE_PATHNAME_PATTERN)?.[1];
}

function RouterProviderInner({
  auth,
  analytics,
}: {
  auth: AuthContextType;
  analytics: AnalyticsContextType;
}) {
  const baseAccess = useAccessContext();
  const pathname = React.useSyncExternalStore(
    (onStoreChange) => router.history.subscribe(onStoreChange),
    () => router.history.location.pathname,
    () => router.history.location.pathname,
  );
  const workspaceSlug = getWorkspaceSlugFromPathname(pathname);

  const access = React.useMemo<RouteAccessContext>(
    () => ({
      ...baseAccess,
      activeWorkspaceId: baseAccess.me ? resolveActiveWorkspace(baseAccess.me, { workspaceSlug }) : null,
    }),
    [baseAccess, workspaceSlug],
  );

  useClearWorkspaceCacheOnChange(queryClient, access.activeWorkspaceId, auth.isAuthenticated);

  return <TanStackRouterProvider router={router} context={{ ...context, auth, access, analytics }} />;
}

/**
 * The RouterProvider component is responsible for providing the router context and
 * history (state) to the application. Use `useRouteContext` to access the router context,
 * like `queryClient`, `auth`, `access`, etc.
 *
 * @returns The RouterProvider component.
 */
function RouterProvider() {
  const auth = useAuth();
  const analytics = useAnalytics();

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProviderInner auth={auth} analytics={analytics} />
    </QueryClientProvider>
  );
}

export { RouterProvider, router };
